import type { ReactNode } from 'react';

import { BrandMark } from '../../components/brand-mark';

/**
 * The frame every unauthenticated screen sits in.
 *
 * Edge to edge, no outer card and no page margin. Left panel at 45%: a
 * photograph above, solid navy below. Right panel: the form on white.
 *
 * ## Why the composition changed twice
 *
 * First it was a centred white card, which floated in an empty grey field.
 * Then it was a bare navy panel beside a form, which was emptier still — two
 * large flat areas and nothing composed between them. This gives the navy
 * something to sit under and the form something to sit beside.
 *
 * ## The photograph
 *
 * A caregiver with a client in a home kitchen — licensed, from Pexels. It
 * replaced a watermarked stock comp that was never fit to ship.
 *
 * Decorative, so `alt=""`: it says nothing a person needs, and describing it
 * before the email field would be noise on the one screen where speed matters
 * most.
 *
 * `object-cover` rather than `contain` — the block is a crop, not a frame, and
 * letterboxing a photograph inside a coloured panel is how a considered layout
 * starts looking like a slide deck.
 *
 * **Re-encoded before it was committed.** The source was a 3794×5691 JPEG at
 * 3.9MB, which is thirteen times the 300KB budget and would have been the
 * slowest thing on the first screen of the product. It ships as WebP at
 * 1400×2100 and 250KB — 1400 wide covers the widest panel this slot reaches at
 * 1x and stays sharp on a laptop at 2x. Anything replacing it goes through the
 * same treatment; a 4MB file at this path is a regression nothing would catch.
 */

/** Where the cover photograph lives. One line to change. */
const COVER_IMAGE = '/auth-cover.webp';

export function AuthFrame({
  title,
  /** Hide the title visually where the action below already says it. */
  titleHidden = false,
  lede,
  children,
}: {
  title: string;
  titleHidden?: boolean;
  lede?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="split:h-dvh split:grid split:grid-cols-[45%_1fr] split:overflow-hidden flex min-h-dvh flex-col">
      {/*
        55/45, and `minmax(0, …)` is what makes it hold.

        A bare `11fr 9fr` has an automatic minimum of `min-content`, so the
        photograph's intrinsic height becomes the row's floor. With a landscape
        source that was invisible; the moment the image became a 2:3 portrait
        the top row claimed ~85% and squeezed the navy to a sliver at the
        bottom. `minmax(0, …)` removes the floor, and the image below is taken
        out of flow so it cannot contribute one in the first place — either
        alone is fragile, both together mean the ratio does not depend on which
        photograph is in the slot.
      */}
      <aside className="bg-rail split:grid split:grid-rows-[minmax(0,11fr)_minmax(0,9fr)] flex flex-col">
        {/* The photograph. Gone entirely below the split — on a phone it would
            be the whole first screen, and the form is what the person came
            for. */}
        <div className="split:block relative hidden overflow-hidden">
          <img
            src={COVER_IMAGE}
            alt=""
            /*
             * `50% 30%` keeps the crop on the three things that carry the
             * picture: the caregiver's face at the upper left, the client's
             * face at centre right, and the hands between them.
             *
             * The source is a 2:3 portrait and this slot is landscape, so only
             * about half its height survives — the two faces span 19% to 65%,
             * which fits, but barely. Centred it cut the caregiver off the top;
             * at 35% her face was pressed into the corner with no headroom; at
             * 30% she has room and the client's chin is still inside the bottom
             * edge. There is no value that gives either of them margin.
             *
             * Absolutely positioned so the image contributes nothing to the
             * grid's sizing — see the row note above.
             */
            className="absolute inset-0 h-full w-full object-cover [object-position:50%_30%]"
            // Above the fold on the first screen of the product; there is
            // nothing to defer it behind.
            fetchPriority="high"
          />
        </div>

        {/* The navy block. A compact band below the split, carrying the mark
            and the welcome line only. */}
        <div className="split:justify-start split:px-12 split:pt-12 flex min-h-0 flex-col px-6 py-5">
          <div className="split:justify-center flex">
            <BrandMark on="rail" />
          </div>

          <div className="border-rail-ink-muted split:mt-8 border-l-edge mt-4 pl-5">
            <p className="text-rail-ink text-md">
              Welcome back to <span className="text-rail-ink-strong font-semibold">Home Care</span>
            </p>
            {/* Quiet, and hidden on the band — every line there pushes the form
                further down a screen the form should be at the top of. */}
            <p className="text-rail-ink-muted split:block mt-2 hidden text-sm">
              Schedules, visits, caregivers and billing for your agency, in one place.
            </p>
          </div>
        </div>
      </aside>

      {/*
        White, and the form sits near the top rather than dead centre below the
        split: a form floating in the middle of a tall empty white field is the
        same emptiness the centred card had, moved.

        Above the split it is vertically centred against the left panel, and it
        is the only thing that scrolls — the navy never slides away from the
        form it anchors.
      */}
      <main className="bg-surface split:items-center split:py-12 split:overflow-y-auto flex flex-1 items-start justify-center px-6 pb-12 pt-8">
        <div className="w-full max-w-sm">
          <header className={titleHidden ? 'sr-only' : 'mb-6'}>
            {/*
              Kept as a real `h1` even when it is not drawn.

              Sign in has no visible heading: a heading directly above a button
              that says the same word is a doubled word doing no work, and the
              mark on the left has already said where you are. None of that is
              true for someone hearing the page rather than seeing it, who
              arrives at a form with no name on it.
            */}
            <h1 className="text-ink text-xl font-bold">{title}</h1>
            {lede && <p className="text-ink-3 text-md mt-2">{lede}</p>}
          </header>

          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * The quiet link under the card's action — "Back to sign in".
 *
 * Directly beneath the button rather than stranded below a gap: the button is
 * full width, so a secondary link sharing its row would be competing for the
 * same space and losing, and one floated far under it reads as belonging to
 * nothing.
 *
 * Sign in puts its forgot-password link *above* the button instead, right
 * aligned, as the reference does — there it is part of the password field's
 * business rather than an afterthought below the action.
 */
export function AuthAside({ children }: { children: ReactNode }): ReactNode {
  return <p className="text-ink-3 mt-3 text-center text-sm">{children}</p>;
}

/** The link style, everywhere on these screens. One underline treatment. */
export const AUTH_LINK =
  'text-brand hover:text-brand-hover font-medium underline underline-offset-4';
